/**
 * ActionPanelContainer - Boardgame.io統合コンテナコンポーネント
 *
 * 全フェーズの Action Panel を管理する統合コンテナ
 *
 * FRONTEND.md Step.1 準拠:
 * - 状態管理をここに集約
 * - adapter関数でPropsを生成
 * - スケルトンコンポーネントに渡す
 *
 * @see FRONTEND.md
 * @see src/adapters/gameStateToUIProps.ts
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import type { BoardProps } from 'boardgame.io/react';
import type { BarbadosGameState, BarbadosPlayer, Hex, HexCoordinate, TileType, BuildSpecialLinkArgs } from '../types';
import type { TransportableGoods, GoodsDisplayBoardProps } from '../types/ui-interfaces';
import { GameBoard } from './GameBoard';
import { GameEndScreen } from './GameEndScreen';
import { getUIConfigByMapName } from '../config/gameRegistry';
import { GameStartModal } from './GameStartModal';
import { IssueSharesPhase } from './phases/IssueSharesPhase';
import { SelectActionsBarbadosPhase as SelectActionsPhase } from './phases/SelectActionsBarbadosPhase';
import { SelectActionsPuertoRicoPhase } from './phases/SelectActionsPuertoRicoPhase';
import { SelectActionsDetroitPhase } from './phases/SelectActionsDetroitPhase';
import { SelectActionsSeattlePhase } from './phases/SelectActionsSeattlePhase';
import { BuildTrackPhase } from './phases/BuildTrackPhase';
import { MoveGoodsPhase } from './phases/MoveGoodsPhase';
import { MoveGoodsSeattlePhase } from './phases/MoveGoodsSeattlePhase';
import { GoodsGrowthSeattlePhase } from './phases/GoodsGrowthSeattlePhase';
import { RefillGoodsPhase } from './phases/RefillGoodsPhase';
import { AutoPhase } from './phases/AutoPhase';
import {
  createIssueSharesProps,
  createSelectActionsProps,
  createBuildTrackProps,
  createMoveGoodsProps,
  createRefillGoodsProps,
  createAutoPhaseProps,
  createPlayerStatsProps,
  findAllRoutes,
  createSeattleOverlayData,
} from '../adapters/gameStateToUIProps';
import { calculateOptimalRotation } from '../adapters/tileRotationHelper';
import { validateBuildSpecialLink } from '../game/validators/buildSpecialLinkValidator';
import { canUndoToTurnStart } from '../game/utils/turnStartSnapshot';
import { useTranslations, useValidationTranslator } from '../i18n';
import { debug } from '../utils/debug';
import { BASE_PATH } from '../config';
import { SEATTLE_DICE_GROUPS } from '../../data/map/seattle_metro/config';

/**
 * Boardgame.io統合ボードコンポーネント（Barbados専用）
 * 全フェーズの状態管理とコンポーネント切り替えを担当
 */
// DEBUG: グローバル変数として公開（開発者ツールからアクセス可能）
declare global {
  interface Window {
    __DEBUG_G__: BarbadosGameState | null;
    __DEBUG_ctx__: any;
  }
}

export function ActionPanelContainer(props: BoardProps<BarbadosGameState>) {
  const { G, ctx, moves, playerID } = props;
  const isMyTurn = playerID === undefined || playerID === null || playerID === ctx.currentPlayer;
  const t = useTranslations();
  const { translateBuildTrackError } = useValidationTranslator();

  // DEBUG: グローバル変数に保存
  if (typeof window !== 'undefined') {
    window.__DEBUG_G__ = G;
    window.__DEBUG_ctx__ = ctx;
  }

  // ============================================================================
  // リプレイ記録（ターン境界スナップショット方式）
  // ============================================================================
  const initialGRef = useRef<unknown>(null);
  const turnSnapshotsRef = useRef<Array<{ turn: number; G: unknown }>>([]);
  const prevTurnRef = useRef<number>(0);

  // 初期状態キャプチャ（actualTurn === 1 の最初のレンダリング時）
  if (G.actualTurn === 1 && initialGRef.current === null) {
    initialGRef.current = JSON.parse(JSON.stringify(G));
  }

  // ターン境界でスナップショット取得
  useEffect(() => {
    const currentTurn = G.actualTurn || 1;
    if (prevTurnRef.current !== 0 && prevTurnRef.current !== currentTurn) {
      // ターンが変わったので前ターン終了時の状態を保存（moveLogは除外してサイズ削減）
      const gClone = JSON.parse(JSON.stringify(G));
      delete gClone.moveLog;
      turnSnapshotsRef.current.push({
        turn: prevTurnRef.current,
        G: gClone,
      });
    }
    prevTurnRef.current = currentTurn;
  }, [G.actualTurn]);

  // ゲーム終了時に最終スナップショットを保存（同期的にキャプチャ）
  // ※ useEffect だとレンダリング後に実行されるため、同一サイクルで
  //    GameEndScreen に渡す replayData に間に合わない問題がある
  const isGameOver = ctx.gameover !== undefined || G.isGameOver;
  if (isGameOver && (prevTurnRef.current > 0 || G.actualTurn >= 1)) {
    const currentTurn = G.actualTurn || 1;
    const alreadyCaptured = turnSnapshotsRef.current.some(s => s.turn === currentTurn);
    if (!alreadyCaptured) {
      const gClone = JSON.parse(JSON.stringify(G));
      delete gClone.moveLog;
      turnSnapshotsRef.current.push({
        turn: currentTurn,
        G: gClone,
      });
    }
  }

  // ============================================================================
  // ゲーム開始モーダル状態管理
  // ============================================================================
  const [showStartModal, setShowStartModal] = useState(true);
  /** ルール確認モード（設定から開いた場合true、初回表示はfalse） */
  const [isRulesOnlyMode, setIsRulesOnlyMode] = useState(false);

  // ============================================================================
  // ゲーム終了モーダル状態管理
  // ============================================================================
  const [showGameEndModal, setShowGameEndModal] = useState(true);

  // ============================================================================
  // Phase 1: 株式発行 状態管理
  // ============================================================================
  const [issueCount, setIssueCount] = useState(0);

  // ============================================================================
  // Phase 4: 線路建設 状態管理
  // ============================================================================
  const [selectedTileType, setSelectedTileType] = useState<TileType | null>(null);
  const [temporaryPlacement, setTemporaryPlacement] = useState<{
    hexCoordinate: HexCoordinate;
    rotation: 0 | 1 | 2 | 3 | 4 | 5;
  } | null>(null);

  // 特殊リンク（フェリー/隣接）仮配置状態
  const [temporaryLinkPlacement, setTemporaryLinkPlacement] = useState<{
    linkType: 'ferry' | 'adjacent';
    linkId: string;
    cost: number;
    validationError: string | null;
    validationErrorCode?: string;
  } | null>(null);

  // ============================================================================
  // Phase 5: 商品輸送 状態管理
  // ============================================================================
  const [selectedGoods, setSelectedGoods] = useState<TransportableGoods | null>(null);
  const [selectedRoute, setSelectedRoute] = useState<import('../types/ui-interfaces').BestRoute | null>(null);
  const [showUpgradeConfirm, setShowUpgradeConfirm] = useState(false);
  const [moveGoodsError, setMoveGoodsError] = useState('');

  // ============================================================================
  // 共通: 商品ディスプレイボード 状態管理
  // ============================================================================
  const [showGoodsDisplay, setShowGoodsDisplay] = useState(false);

  // ============================================================================
  // Phase 9: Production 配置 状態管理
  // ============================================================================
  const [productionSelectedCell, setProductionSelectedCell] = useState<{
    side: 'light' | 'dark';
    column: number;
    columnType: 'city' | 'newCity';
    row: number;
  } | null>(null);

  // 選択中の商品キューブのインデックス（0 or 1）
  const [selectedGoodsIndex, setSelectedGoodsIndex] = useState<number | null>(null);

  // 1個目の配置情報を記録（Cancel時にundoするため）
  const [firstPlacementInfo, setFirstPlacementInfo] = useState<{
    side: 'light' | 'dark';
    column: number;
    columnType: 'city' | 'newCity';
    row: number;
    goodsIndex: number;
  } | null>(null);

  // ============================================================================
  // Phase 9: Seattle 商品配置 状態管理（マップ選択用）
  // ============================================================================
  // ハイライト対象のロケーションID（子コンポーネントから受け取る、配置可能のみ）
  const [refillGoodsHighlightedCityIds, setRefillGoodsHighlightedCityIds] = useState<string[]>([]);
  // 出目に対応する全ロケーションID（満杯含む、エラー判定用）
  const [refillGoodsAllLocationIds, setRefillGoodsAllLocationIds] = useState<string[]>([]);
  // マップで選択されたロケーションID
  const [refillGoodsSelectedLocation, setRefillGoodsSelectedLocation] = useState<string | null>(null);
  // 配置不可エラー（クリックした City/Town が満杯の場合）
  const [refillGoodsError, setRefillGoodsError] = useState<{ locationId: string; coordinate: { q: number; r: number } } | null>(null);

  // ============================================================================
  // Phase 4: Hexクリックハンドラー
  // ============================================================================
  const handleHexClick = useCallback(
    (hex: Hex) => {
      // Phase 9 (refillGoods): Seattle 商品配置 - マップ上でロケーション選択
      if (ctx.phase === 'refillGoods' && refillGoodsAllLocationIds.length > 0) {
        const clickedLocationId = hex.city?.id || hex.town?.id;
        if (clickedLocationId) {
          // 出目に対応する都市かチェック
          if (!refillGoodsAllLocationIds.includes(clickedLocationId)) {
            // 出目に関係ない都市 → 何もしない
            return;
          }
          // 配置可能かチェック
          if (refillGoodsHighlightedCityIds.includes(clickedLocationId)) {
            setRefillGoodsSelectedLocation(clickedLocationId);
            setRefillGoodsError(null);
          } else {
            // 出目に対応するが満杯 → エラーバルーン表示
            setRefillGoodsError({ locationId: clickedLocationId, coordinate: hex.coordinate });
            setRefillGoodsSelectedLocation(null);
            // 3秒後にエラーをクリア
            setTimeout(() => setRefillGoodsError(null), 3000);
          }
        }
        return;
      }

      // Phase 4 (buildTrack): タイル配置
      if (ctx.phase !== 'buildTrack' || !selectedTileType) return;

      // 同じHexをクリック → 時計回り回転
      if (
        temporaryPlacement &&
        temporaryPlacement.hexCoordinate.q === hex.coordinate.q &&
        temporaryPlacement.hexCoordinate.r === hex.coordinate.r
      ) {
        const newRotation = ((temporaryPlacement.rotation + 1) % 6) as 0 | 1 | 2 | 3 | 4 | 5;
        setTemporaryPlacement({
          hexCoordinate: hex.coordinate,
          rotation: newRotation,
        });
      } else {
        // 新規仮配置 - 最適回転を計算
        const optimalRotation = calculateOptimalRotation(
          selectedTileType,
          G.map,
          hex.coordinate,
          G.unfinishedSections || []
        );
        setTemporaryPlacement({
          hexCoordinate: hex.coordinate,
          rotation: optimalRotation,
        });
      }
    },
    [ctx.phase, selectedTileType, temporaryPlacement, G.map, G.unfinishedSections, refillGoodsHighlightedCityIds, refillGoodsAllLocationIds]
  );

  // 右クリックで反時計回り回転
  const handleHexRightClick = useCallback(
    (hex: Hex) => {
      if (ctx.phase !== 'buildTrack' || !selectedTileType) return;

      // 同じHexを右クリック → 反時計回り回転
      if (
        temporaryPlacement &&
        temporaryPlacement.hexCoordinate.q === hex.coordinate.q &&
        temporaryPlacement.hexCoordinate.r === hex.coordinate.r
      ) {
        const newRotation = ((temporaryPlacement.rotation - 1 + 6) % 6) as 0 | 1 | 2 | 3 | 4 | 5;
        setTemporaryPlacement({
          hexCoordinate: hex.coordinate,
          rotation: newRotation,
        });
      }
    },
    [ctx.phase, selectedTileType, temporaryPlacement]
  );

  // Phase終了判定
  const isPhaseComplete = ctx.gameover !== undefined || ctx.phase === null;

  // ============================================================================
  // Phase別コンテンツ生成
  // ============================================================================
  // buildTrackのvalidationErrorを保持（getPhaseContent内で設定）
  let buildTrackValidationError: string | null = null;
  let buildTrackValidationErrorCode: string | undefined = undefined;

  const getPhaseContent = () => {
    // 注: 旧アニメーション表示は削除（手動クリック進行に変更したため）

    if (isPhaseComplete) {
      return (
        <div className="">
          <h2 className="">Phase Completed!</h2>
          <p className="">Proceeding to next phase...</p>
        </div>
      );
    }

    switch (ctx.phase) {
      case 'issueShares': {
        const issueSharesProps = createIssueSharesProps(G, isMyTurn, moves as any);
        return (
          <IssueSharesPhase
            {...issueSharesProps}
            issueCount={issueCount}
            onIssueCountChange={setIssueCount}
          />
        );
      }

      case 'selectActions': {
        // Detroit: 専用UIコンポーネント
        if (G.mapName === 'Detroit Bankruptcy Solo') {
          return (
            <SelectActionsDetroitPhase
              G={G as any}
              isMyTurn={isMyTurn}
              onSelectAction={(action) => moves.selectAction({ action })}
              onSkip={() => moves.skipAction()}
            />
          );
        }
        // Puerto Rico: 専用UIコンポーネント
        if (G.mapName === 'Puerto Rico') {
          const player = G.players['0'];
          return (
            <SelectActionsPuertoRicoPhase
              playerCash={player.cash}
              locomotiveLevel={player.locomotiveLevel}
              isMyTurn={isMyTurn}
              onSelectAction={(action) => moves.selectAction(action)}
              onSkip={() => moves.skipAction()}
            />
          );
        }
        // Seattle Metro Solo: 専用UIコンポーネント
        if (G.mapName === 'Seattle Metro Solo') {
          return (
            <SelectActionsSeattlePhase
              G={G as any}
              isMyTurn={isMyTurn}
              onSelectAction={(action, locomotiveEffect) => moves.selectAction({ action, locomotiveEffect })}
              onCancel={() => moves.cancelSelection()}
              onConfirm={() => moves.confirmSelection()}
            />
          );
        }
        // Barbados: 従来のUIコンポーネント
        const selectActionsProps = createSelectActionsProps(G, isMyTurn, moves as any);
        return <SelectActionsPhase {...selectActionsProps} />;
      }

      case 'buildTrack': {
        const buildTrackProps = createBuildTrackProps(
          G,
          ctx,
          isMyTurn,
          moves as any,
          selectedTileType && temporaryPlacement
            ? {
                hexCoordinate: temporaryPlacement.hexCoordinate,
                tileType: selectedTileType,
                rotation: temporaryPlacement.rotation,
              }
            : null,
          (placement: { hexCoordinate: HexCoordinate; rotation: 0 | 1 | 2 | 3 | 4 | 5 } | null) => {
            if (placement) {
              setTemporaryPlacement({
                hexCoordinate: placement.hexCoordinate,
                rotation: placement.rotation,
              });
            } else {
              setTemporaryPlacement(null);
            }
          }
        );

        // validationErrorを外部変数に保存（temporaryTilePlacementで使用）
        buildTrackValidationErrorCode = buildTrackProps.temporaryPlacement?.validationErrorCode;
        buildTrackValidationError = translateBuildTrackError(
          buildTrackValidationErrorCode,
          buildTrackProps.temporaryPlacement?.validationError || null
        );

        return (
          <BuildTrackPhase
            {...buildTrackProps}
            selectedTileType={selectedTileType}
            temporaryLinkPlacement={temporaryLinkPlacement}
            onSelectTile={(tileType) => {
              setSelectedTileType(tileType);
              setTemporaryPlacement(null);
              setTemporaryLinkPlacement(null);  // リンク仮配置もクリア
            }}
            onConfirmPlacement={() => {
              // 特殊リンク仮配置がある場合は特殊リンクを敷設
              if (temporaryLinkPlacement && !temporaryLinkPlacement.validationError) {
                (moves as any).buildSpecialLink({
                  linkType: temporaryLinkPlacement.linkType,
                  linkId: temporaryLinkPlacement.linkId,
                });
                setTemporaryLinkPlacement(null);
                return;
              }
              // 通常タイル仮配置
              if (selectedTileType && temporaryPlacement && !buildTrackProps.temporaryPlacement?.validationError) {
                moves.buildTrack({
                  hexCoordinate: temporaryPlacement.hexCoordinate,
                  tileType: selectedTileType,
                  rotation: temporaryPlacement.rotation,
                  placementType: buildTrackProps.temporaryPlacement?.placementType || 'place',
                });
                setTemporaryPlacement(null);
              }
            }}
            onFinishBuild={() => {
              // フェーズ終了時にタイル選択と仮配置状態をクリア
              setSelectedTileType(null);
              setTemporaryPlacement(null);
              setTemporaryLinkPlacement(null);
              moves.passBuildTrack();
            }}
            onCancelAllPlacements={() => {
              // このフェーズ中の全配置を撤去（moveが実装されている場合）
              if (moves.cancelAllBuildTrack) {
                moves.cancelAllBuildTrack();
              }
              setSelectedTileType(null);
              setTemporaryPlacement(null);
              setTemporaryLinkPlacement(null);
            }}
          />
        );
      }

      case 'moveGoods': {
        const moveGoodsProps = createMoveGoodsProps(G, isMyTurn, moves as any, selectedGoods, selectedRoute);

        // 経路サイクルロジック（共通）
        const handleSelectGoodsWithRouteCycle = (goods: TransportableGoods) => {
          const isSameGoods = selectedGoods?.cityId === goods.cityId && selectedGoods?.color === goods.color;
          if (isSameGoods) {
            // 同じ商品をクリックした場合は経路をトグル
            const player = G.players['0'] as BarbadosPlayer;
            const { bestRoute: originalBest, alternativeRoutes } = findAllRoutes(G, goods, player.locomotiveLevel);
            if (!originalBest) return;

            const allRoutes = [originalBest, ...alternativeRoutes];
            if (allRoutes.length <= 1) return; // 複数経路がなければ何もしない

            let currentIndex = 0;
            if (selectedRoute) {
              const foundIndex = allRoutes.findIndex(r =>
                r.destinationId === selectedRoute.destinationId && r.linkCount === selectedRoute.linkCount
              );
              if (foundIndex >= 0) currentIndex = foundIndex;
            }

            const nextIndex = (currentIndex + 1) % allRoutes.length;
            const nextRoute = allRoutes[nextIndex];
            setSelectedRoute(nextIndex === 0 ? null : nextRoute);
          } else {
            // 別の商品を選択した場合は通常の選択処理
            setSelectedGoods(goods);
            setSelectedRoute(null);
            setMoveGoodsError('');
          }
        };

        // Seattle Metro Solo: 共通コンポーネントをラップ
        if (G.mapName === 'Seattle Metro Solo') {
          return (
            <MoveGoodsSeattlePhase
              G={G as any}
              isMyTurn={isMyTurn}
              transportableGoods={moveGoodsProps.transportableGoods}
              selectedGoods={selectedGoods}
              bestRoute={selectedRoute || moveGoodsProps.bestRoute}
              alternativeRoutes={moveGoodsProps.alternativeRoutes}
              showUpgradeConfirm={showUpgradeConfirm}
              onSelectGoods={handleSelectGoodsWithRouteCycle}
              onSelectRoute={(route) => setSelectedRoute(route)}
              onConfirmTransport={(grayCityAction) => {
                const currentRoute = selectedRoute || moveGoodsProps.bestRoute;
                if (selectedGoods && currentRoute) {
                  moves.moveGoods({
                    fromCityId: selectedGoods.cityId,
                    toCityId: currentRoute.destinationId,
                    goodsColor: selectedGoods.color,
                    path: currentRoute.path,
                    grayCityAction,
                  });
                  setSelectedGoods(null);
                  setSelectedRoute(null);
                }
              }}
              onUpgradeLoco={() => {
                moves.upgradeLoco();
                setShowUpgradeConfirm(false);
              }}
              onShowUpgradeConfirm={() => setShowUpgradeConfirm(true)}
              onHideUpgradeConfirm={() => setShowUpgradeConfirm(false)}
              onPass={() => {
                moves.passMoveGoods();
              }}
            />
          );
        }

        return (
          <MoveGoodsPhase
            {...moveGoodsProps}
            selectedGoods={selectedGoods}
            showUpgradeConfirm={showUpgradeConfirm}
            errorMessage={moveGoodsError}
            onSelectGoods={handleSelectGoodsWithRouteCycle}
            onSelectRoute={(route) => setSelectedRoute(route)}
            onConfirmTransport={() => {
              const currentRoute = selectedRoute || moveGoodsProps.bestRoute;
              if (selectedGoods && currentRoute) {
                moves.moveGoods({
                  fromCityId: selectedGoods.cityId,
                  toCityId: currentRoute.destinationId,
                  goodsColor: selectedGoods.color,
                  path: currentRoute.path,
                });
                setSelectedGoods(null);
                setSelectedRoute(null);
                setMoveGoodsError('');
              }
            }}
            onUpgradeLoco={() => {
              moves.upgradeLoco();
              setShowUpgradeConfirm(false);
            }}
            onShowUpgradeConfirm={() => setShowUpgradeConfirm(true)}
            onHideUpgradeConfirm={() => setShowUpgradeConfirm(false)}
            onPass={() => {
              moves.passMoveGoods();
            }}
          />
        );
      }

      case 'collectIncome': {
        const autoProps = createAutoPhaseProps(G, 6);
        return (
          <AutoPhase
            {...autoProps}
            floatingText={G.autoPhaseResult?.floatingText}
            onProceed={() => moves.proceedAutoPhase()}
          />
        );
      }

      case 'payExpenses': {
        const autoProps = createAutoPhaseProps(G, 7);
        return (
          <AutoPhase
            {...autoProps}
            floatingText={G.autoPhaseResult?.floatingText}
            onProceed={() => moves.proceedAutoPhase()}
          />
        );
      }

      case 'incomeReduction': {
        const autoProps = createAutoPhaseProps(G, 8);
        return (
          <AutoPhase
            {...autoProps}
            floatingText={G.autoPhaseResult?.floatingText}
            onProceed={() => moves.proceedAutoPhase()}
          />
        );
      }

      case 'refillGoods': {
        const player = G.players['0'];

        // Seattle Metro Solo: 専用UIコンポーネント
        if (G.mapName === 'Seattle Metro Solo') {
          const seattleG = G as any;
          const pending = seattleG.pendingGoodsPlacement || { remainingDice: [], remainingCubes: [] };
          const airportAutoPlacement = seattleG.autoPhaseResult?.details?.airportCube
            ? { color: seattleG.autoPhaseResult.details.airportCube }
            : undefined;
          // Airport 満杯によるゲームオーバー状態を検出（Request #5）
          const isAirportOverload = seattleG.autoPhaseResult?.details?.airportOverload === true;

          // 10ターン目スキップ時はAutoPhaseと同じ表示
          if (seattleG.actualTurn >= 10 && seattleG.autoPhaseResult) {
            const autoProps = createAutoPhaseProps(G, 9);
            return (
              <AutoPhase
                {...autoProps}
                floatingText={seattleG.autoPhaseResult.floatingText}
                onProceed={() => moves.proceedAutoPhase()}
              />
            );
          }

          // ダイス目からロケーションIDを取得するヘルパー
          const getLocationsByDice = (diceValues: number[]) => {
            const availableIds: string[] = [];
            const allIds: string[] = [];
            const uniqueDice = [...new Set(diceValues)];
            for (const diceValue of uniqueDice) {
              const diceGroup = SEATTLE_DICE_GROUPS.find((g) => g.diceValue === diceValue);
              if (diceGroup) {
                for (const locId of diceGroup.locationIds) {
                  if (!allIds.includes(locId)) {
                    allIds.push(locId);
                    const hex = seattleG.map.find((h: any) => h.city?.id === locId || h.town?.id === locId);
                    if (hex && (hex.goods?.length || 0) < 2) {
                      availableIds.push(locId);
                    }
                  }
                }
              }
            }
            return { availableIds, allIds };
          };

          return (
            <GoodsGrowthSeattlePhase
              G={seattleG}
              isMyTurn={isMyTurn}
              remainingDice={pending.remainingDice}
              remainingCubes={pending.remainingCubes}
              airportAutoPlacement={airportAutoPlacement}
              isAirportOverload={isAirportOverload}
              selectedMapLocation={refillGoodsSelectedLocation}
              onPlaceGoods={(cubeIndex, locationId) => {
                moves.placeGoods({ cubeIndex, locationId });
                // 配置後に状態をリセット
                setRefillGoodsHighlightedCityIds([]);
                setRefillGoodsAllLocationIds([]);
                setRefillGoodsSelectedLocation(null);
              }}
              onProceed={() => moves.proceedAutoPhase()}
              onGameOver={() => moves.triggerDemandOverload()}
              onHighlightLocations={(availableIds, allIds) => {
                setRefillGoodsHighlightedCityIds(availableIds);
                setRefillGoodsAllLocationIds(allIds);
              }}
              onCancelSelection={() => {
                // バックエンドで配置をリセット
                moves.resetGoodsPlacement();
                // UI 状態をリセット
                setRefillGoodsHighlightedCityIds([]);
                setRefillGoodsAllLocationIds([]);
                setRefillGoodsSelectedLocation(null);
              }}
              getLocationsByDice={getLocationsByDice}
            />
          );
        }

        const placements = (G as any).lastGoodsPlacement as Array<{
          color: string;
          cityName: string;
          diceValue: number;
          cityType: 'city' | 'newCity';
          side: 'light' | 'dark';
        }> | undefined;
        const lastDiceRolls = (G as any).lastDiceRolls as [number, number] | undefined;

        // Production選択時: ダイスロール前は手動配置UI、ダイスロール後は結果表示
        if (player.selectedAction === 'PRODUCTION') {
          // ダイスロールが実行された後（triggerDiceRollForProduction呼び出し後）
          if (lastDiceRolls && G.autoPhaseResult) {
            return (
              <div className="space-y-3">
                <button
                  className="w-full p-4 bg-muted/50 rounded text-center transition-all hover:bg-primary/20 hover:border-primary cursor-pointer border-2 border-transparent"
                  onClick={() => moves.proceedAutoPhase()}
                >
                  <h2 className="font-heading text-lg font-semibold">{G.autoPhaseResult.floatingText}</h2>

                  {/* 配置された商品 */}
                  {placements && placements.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {placements.map((p, i) => (
                        <div key={i} className="text-sm">
                          <span className={`inline-block w-3 h-3 rounded-sm mr-1 bg-goods-${p.color}`}></span>
                          {p.color} → {p.cityType === 'newCity' ? p.cityName : `${p.side === 'dark' ? 'Dark' : 'Light'} ${p.diceValue}`}
                        </div>
                      ))}
                    </div>
                  )}

                  {placements && placements.length === 0 && (
                    <p className="text-xs text-muted-foreground mt-1">{t.refillGoods.noGoodsPlaced}</p>
                  )}
                </button>

                <div className="text-xs text-primary text-center font-medium">
                  {t.refillGoods.clickToProceed}
                </div>
              </div>
            );
          }

          // ダイスロール前: 手動配置UI（RefillGoodsPhase）
          const refillProps = createRefillGoodsProps(
            G,
            moves as any,
            () => setShowGoodsDisplay(true),
            () => setShowGoodsDisplay(false),
            showGoodsDisplay
          );
          return <RefillGoodsPhase {...refillProps} />;
        }

        // 非Production: ダイス結果表示とクリック待機
        if (G.autoPhaseResult) {
          return (
            <div className="space-y-3">
              <button
                className="w-full p-4 bg-muted/50 rounded text-center transition-all hover:bg-primary/20 hover:border-primary cursor-pointer border-2 border-transparent"
                onClick={() => moves.proceedAutoPhase()}
              >
                <h2 className="font-heading text-lg font-semibold">{G.autoPhaseResult.floatingText}</h2>

                {/* 配置された商品 */}
                {placements && placements.length > 0 && (
                  <div className="mt-3 space-y-1">
                    {placements.map((p, i) => (
                      <div key={i} className="text-sm">
                        <span className={`inline-block w-3 h-3 rounded-sm mr-1 bg-goods-${p.color}`}></span>
                        {p.color} → {p.cityType === 'newCity' ? p.cityName : `${p.side === 'dark' ? 'Dark' : 'Light'} ${p.diceValue}`}
                      </div>
                    ))}
                  </div>
                )}

                {placements && placements.length === 0 && (
                  <p className="text-xs text-muted-foreground mt-1">{t.refillGoods.noGoodsPlaced}</p>
                )}
              </button>

              <div className="text-xs text-primary text-center font-medium">
                {t.refillGoods.clickToProceed}
              </div>
            </div>
          );
        }

        // フォールバック（通常はここに来ない）
        return (
          <div className="space-y-4">
            <p className="text-muted-foreground text-sm">Rolling dice...</p>
          </div>
        );
      }

      case 'advanceTurn': {
        const autoProps = createAutoPhaseProps(G, 10);
        return (
          <AutoPhase
            {...autoProps}
            floatingText={G.autoPhaseResult?.floatingText}
            onProceed={() => moves.proceedAutoPhase()}
          />
        );
      }

      default:
        return (
          <div className="">
            <h3 className="">{ctx.phase}</h3>
            <p className="">UI not yet implemented</p>
          </div>
        );
    }
  };

  // ============================================================================
  // GameBoard Props 生成
  // ============================================================================
  // Puerto Rico: goodsDisplay ボタンは不要（マップにgoodsDisplayがない）
  const showGoodsDisplayCallback = G.mapName === 'Puerto Rico' ? undefined : () => setShowGoodsDisplay(true);
  const playerStatsProps = createPlayerStatsProps(G, showGoodsDisplayCallback);

  // getPhaseContentを先に呼び出し、buildTrackValidationErrorを設定
  const rawPhaseContent = getPhaseContent();

  // ゲーム終了時は「結果を見る」ボタンを追加
  const phaseContent = isGameOver && !showGameEndModal ? (
    <div className="space-y-4">
      {rawPhaseContent}
      <button
        className="btn-primary w-full py-2 text-sm font-medium"
        onClick={() => setShowGameEndModal(true)}
      >
        {t.gameEnd.viewScore}
      </button>
    </div>
  ) : rawPhaseContent;

  // 仮配置タイル情報（buildTrackValidationErrorが設定された後に計算）
  // getPhaseContent()内でbuildTrackPropsから取得
  const getBuildTrackCost = () => {
    if (ctx.phase !== 'buildTrack' || !selectedTileType || !temporaryPlacement) return undefined;
    const props = createBuildTrackProps(
      G,
      ctx,
      isMyTurn,
      moves as any,
      {
        hexCoordinate: temporaryPlacement.hexCoordinate,
        tileType: selectedTileType,
        rotation: temporaryPlacement.rotation,
      },
      () => {}
    );
    return props.temporaryPlacement?.cost;
  };

  const temporaryTilePlacement =
    selectedTileType && temporaryPlacement
      ? {
          hexCoordinate: temporaryPlacement.hexCoordinate,
          tileType: selectedTileType,
          rotation: temporaryPlacement.rotation,
          validationError: buildTrackValidationError,
          validationErrorCode: buildTrackValidationErrorCode,
          cost: !buildTrackValidationError ? getBuildTrackCost() : undefined,
        }
      : null;

  // ============================================================================
  // GameEndScreen Props 生成
  // ============================================================================
  const getGameEndProps = () => {
    const player = G.players['0'];
    const buybackCost = player.sharesIssued * 5;
    const finalCash = player.finalCash ?? player.cash - buybackCost;

    // endReason を正規化（Seattle 固有の endReason はそのまま返す）
    const normalizeEndReason = (): 'VICTORY' | 'DEFEAT' | 'BANKRUPTCY' | 'DEFEAT_DEMAND_OVERLOAD' | 'DEFEAT_SETTLEMENT_FAILED' => {
      // Detroit: 破産がゲーム終了条件だが、isVictorious なら VICTORY
      if (G.endReason === 'BANKRUPTCY' && player.isVictorious) return 'VICTORY';
      if (G.endReason === 'BANKRUPTCY') return 'BANKRUPTCY';
      if (G.endReason === 'VICTORY') return 'VICTORY';
      if (G.endReason === 'DEFEAT') return 'DEFEAT';
      // Seattle: 固有の敗北理由はそのまま返す
      if (G.endReason === 'DEFEAT_DEMAND_OVERLOAD') return 'DEFEAT_DEMAND_OVERLOAD';
      if (G.endReason === 'DEFEAT_SETTLEMENT_FAILED') return 'DEFEAT_SETTLEMENT_FAILED';
      // Puerto Rico: DEFEAT_BLACK_CUBES は DEFEAT に変換
      if (G.endReason === 'DEFEAT_BLACK_CUBES') return 'DEFEAT';
      // turn_limit, all_bankrupt などは VICTORY/DEFEAT に変換
      return finalCash >= 0 ? 'VICTORY' : 'DEFEAT';
    };

    // Puerto Rico 用スコア詳細を生成
    const getPuertoRicoScore = () => {
      if (G.mapName !== 'Puerto Rico') return undefined;

      // マップ上のキューブをカウント
      let remainingBlackCubes = 0;
      let remainingTotalCubes = 0;
      for (const hex of G.map) {
        if (hex.town && hex.goods) {
          remainingTotalCubes += hex.goods.length;
          for (const cube of hex.goods) {
            if (cube === 'black') remainingBlackCubes++;
          }
        }
      }

      const blackCubePenalty = remainingBlackCubes * 10;
      const penalizedIncomeLevel = player.incomeLevel - blackCubePenalty;

      // finalScore は勝利時のみ計算
      let finalScore: number | null = null;
      if (penalizedIncomeLevel >= 0) {
        const incomePoints = penalizedIncomeLevel * 3;
        const linkPoints = G.completedLinks.length;
        const sharePenalty = player.sharesIssued * 3;
        finalScore = incomePoints + linkPoints - sharePenalty;
      }

      return {
        originalIncomeLevel: player.incomeLevel,
        remainingBlackCubes,
        remainingTotalCubes,
        blackCubePenalty,
        penalizedIncomeLevel,
        finalScore,
      };
    };

    // Seattle Metro 用スコア詳細を生成
    const getSeattleScore = () => {
      if (G.mapName !== 'Seattle Metro Solo') return undefined;

      const seattlePlayer = player as any;

      // 清算コスト計算
      const shareBuybackCost = seattlePlayer.sharesIssued * 5;
      const engineerUsageCount = seattlePlayer.engineerUsageCount || 0;
      const locomotiveUsageCount = seattlePlayer.locomotiveUsageCount || 0;
      const actionUsageCost = (engineerUsageCount + locomotiveUsageCount) * 5;
      const settlementTotalCost = shareBuybackCost + actionUsageCost;

      // 清算成功判定: endGameSeattle() が設定した endReason を使用
      // Note: 清算後に player.cash から支払い済みなので、cash比較では判定不可
      const settlementSuccess = G.endReason !== 'DEFEAT_SETTLEMENT_FAILED';

      // 清算前の現金を復元（表示用）
      // 清算成功時: 現在の cash + 支払った settlementTotalCost
      // 清算失敗時: 現在の cash（支払いは発生していない）
      const cashBeforeSettlement = settlementSuccess
        ? seattlePlayer.cash + settlementTotalCost
        : seattlePlayer.cash;

      // 得点計算（清算成功時のみ）
      let remainingGoods = 0;
      for (const hex of G.map) {
        if (hex.goods) {
          remainingGoods += hex.goods.length;
        }
      }

      // 未接続駅数をカウント（Downtown から BFS）
      const connected = new Set<string>();
      const queue: string[] = ['DOWNTOWN'];
      while (queue.length > 0) {
        const current = queue.shift()!;
        if (connected.has(current)) continue;
        connected.add(current);
        for (const link of G.completedLinks) {
          if (link.startId === current && !connected.has(link.endId)) {
            queue.push(link.endId);
          }
          if (link.endId === current && !connected.has(link.startId)) {
            queue.push(link.startId);
          }
        }
      }
      const allStations: string[] = [];
      for (const hex of G.map) {
        if (hex.city?.id) allStations.push(hex.city.id);
        if (hex.town?.id) allStations.push(hex.town.id);
      }
      const unconnectedStations = allStations.filter(id => !connected.has(id)).length;

      const deliveredCubes = seattlePlayer.deliveredCubes || 0;

      // finalScore は endGameSeattle() で計算済みなので、それを使用
      // seattlePlayer.finalScore が存在すれば使用、なければ再計算
      const finalScore = seattlePlayer.finalScore !== undefined && seattlePlayer.finalScore !== null
        ? seattlePlayer.finalScore
        : (settlementSuccess ? (deliveredCubes * 5) - remainingGoods - unconnectedStations : null);

      return {
        shareBuybackCost,
        actionUsageCost,
        settlementTotalCost,
        settlementSuccess,
        cash: cashBeforeSettlement,
        deliveredCubes,
        remainingGoods,
        unconnectedStations,
        finalScore,
        engineerUsageCount,
        locomotiveUsageCount,
        sharesIssued: seattlePlayer.sharesIssued,
        cargoTransportLevel: (G as any).cargoTransportLevel || 2,
      };
    };

    // isVictorious を判定（マップ別）
    const determineIsVictorious = (): boolean => {
      // player.isVictorious があればそれを使用
      if (player.isVictorious !== undefined) return player.isVictorious;
      // Barbados: finalCash >= 0 で勝利
      return finalCash >= 0;
    };

    // ランキング用: 統一最終スコアを計算
    const getUnifiedFinalScore = (): number => {
      const endScreen = getUIConfigByMapName(G.mapName || '').endScreen;
      switch (endScreen.scoreType) {
        case 'cash':
          return player.finalCash ?? (player.cash - player.sharesIssued * (endScreen.shareBuybackCost || 5));
        case 'income':
          return getPuertoRicoScore()?.finalScore ?? 0;
        case 'turns':
          return (G as any).actualTurn || 10;
        case 'finalScore':
          return getSeattleScore()?.finalScore ?? 0;
        default:
          return 0;
      }
    };

    // ランキング用: メタデータ（全マップ共通 + マップ固有）
    const getScoreMetadata = (): Record<string, unknown> => {
      const base: Record<string, unknown> = {
        cash: player.cash,
        sharesIssued: player.sharesIssued,
        incomeLevel: player.incomeLevel,
        locomotiveLevel: player.locomotiveLevel,
        completedLinks: G.completedLinks.length,
        undoCount: G.undoCount || 0,
        unifiedFinalScore: getUnifiedFinalScore(),
      };
      const seattle = getSeattleScore();
      if (seattle) {
        base.cargoTransportLevel = seattle.cargoTransportLevel;
        base.deliveredCubes = seattle.deliveredCubes;
        base.remainingGoods = seattle.remainingGoods;
        base.unconnectedStations = seattle.unconnectedStations;
      }
      const pr = getPuertoRicoScore();
      if (pr) {
        base.remainingBlackCubes = pr.remainingBlackCubes;
        base.remainingTotalCubes = pr.remainingTotalCubes;
      }
      return base;
    };

    const { apiMapType } = getUIConfigByMapName(G.mapName || '');

    return {
      endReason: normalizeEndReason(),
      player: {
        cash: player.cash,
        sharesIssued: player.sharesIssued,
        incomeLevel: player.incomeLevel,
        locomotiveLevel: player.locomotiveLevel,
        finalCash: player.finalCash ?? null,
        isVictorious: determineIsVictorious(),
      },
      completedLinksCount: G.completedLinks.length,
      actualTurn: (G as any).actualTurn || 10,
      onNewGame: () => window.location.reload(),
      onClose: () => setShowGameEndModal(false),
      mapName: G.mapName,
      puertoRicoScore: getPuertoRicoScore(),
      seattleScore: getSeattleScore(),
      apiMapType,
      unifiedFinalScore: getUnifiedFinalScore(),
      scoreMetadata: getScoreMetadata(),
      replayData: initialGRef.current
        ? {
            initialState: initialGRef.current,
            turnSnapshots: turnSnapshotsRef.current,
            moveLog: G.moveLog,
          }
        : undefined,
    };
  };

  // ============================================================================
  // Production Mode 設定（Phase 9 で PRODUCTION 選択時）
  // ============================================================================
  const player = G.players['0'];
  const isProductionPhase = ctx.phase === 'refillGoods' && player?.selectedAction === 'PRODUCTION';

  // 配置済みフラグを計算（各キューブが配置されたかどうか）
  const calculatePlacedFlags = (): [boolean, boolean] => {
    const placedCount = G.productionPlacementCount || 0;
    // firstPlacementInfoのgoodsIndexに基づいてどのキューブが配置されたか判断
    if (placedCount === 0) {
      return [false, false];
    } else if (placedCount === 1 && firstPlacementInfo) {
      return firstPlacementInfo.goodsIndex === 0 ? [true, false] : [false, true];
    } else {
      return [true, true];
    }
  };

  const productionMode: GoodsDisplayBoardProps['productionMode'] = isProductionPhase && G.drawnGoods
    ? {
        drawnGoods: G.drawnGoods,
        placedFlags: calculatePlacedFlags(),
        placedCount: G.productionPlacementCount || 0,
        selectedGoodsIndex: selectedGoodsIndex,
        selectedCell: productionSelectedCell,
        onGoodsClick: (index: number) => {
          setSelectedGoodsIndex(index);
          setProductionSelectedCell(null);
        },
        onCellClick: (side, column, columnType, row) => {
          // 商品が選択されていない場合は何もしない
          if (selectedGoodsIndex === null) return;
          setProductionSelectedCell({ side, column, columnType, row });
        },
        onConfirmPlacement: () => {
          if (!productionSelectedCell || !G.drawnGoods || selectedGoodsIndex === null) return;

          const placedCount = G.productionPlacementCount || 0;
          const color = G.drawnGoods[selectedGoodsIndex];

          // 1個目の配置情報を記録（Cancel時のundo用）
          if (placedCount === 0) {
            setFirstPlacementInfo({
              side: productionSelectedCell.side,
              column: productionSelectedCell.column,
              columnType: productionSelectedCell.columnType,
              row: productionSelectedCell.row,
              goodsIndex: selectedGoodsIndex,
            });
          }

          moves.executeProduction({
            placements: [{
              side: productionSelectedCell.side,
              column: productionSelectedCell.column,
              columnType: productionSelectedCell.columnType,
              row: productionSelectedCell.row,
              color,
            }],
          });

          setProductionSelectedCell(null);
          setSelectedGoodsIndex(null);
        },
        onCancelPlacement: () => {
          const placedCount = G.productionPlacementCount || 0;

          if (productionSelectedCell || selectedGoodsIndex !== null) {
            // セル選択中または商品選択中の場合は選択をクリア
            setProductionSelectedCell(null);
            setSelectedGoodsIndex(null);
          } else if (placedCount > 0) {
            // 配置済みがある場合は全てリセット（スナップショットから復元）
            moves.resetProductionPlacement();
            setFirstPlacementInfo(null);
            setSelectedGoodsIndex(null);
            setProductionSelectedCell(null);
          }
        },
        onFinishPlacement: () => {
          // 2個とも配置完了したらダイスロールを実行
          moves.triggerDiceRollForProduction();
          // モーダルを閉じて状態をクリア
          setShowGoodsDisplay(false);
          setFirstPlacementInfo(null);
          setSelectedGoodsIndex(null);
          setProductionSelectedCell(null);
        },
      }
    : undefined;

  return (
    <>
      <GameBoard
        hexes={G.map}
        goodsDisplay={G.goodsDisplay}
        phaseContent={phaseContent}
        onHexClick={(ctx.phase === 'buildTrack' || (ctx.phase === 'refillGoods' && refillGoodsHighlightedCityIds.length > 0)) ? handleHexClick : undefined}
        onHexRightClick={ctx.phase === 'buildTrack' ? handleHexRightClick : undefined}
        temporaryTilePlacement={temporaryTilePlacement}
        showGoodsDisplay={showGoodsDisplay}
        onSetShowGoodsDisplay={setShowGoodsDisplay}
        productionMode={productionMode}
        playerStatsProps={playerStatsProps}
        phaseName={ctx.phase || 'Completed'}
        currentTurn={G.actualTurn || 1}
        mapName={G.mapName}
        showGoodsDisplayButton={G.mapConfig?.hasGoodsDisplay !== false}
        initialOrientation={G.mapConfig?.initialOrientation}
        ferryLinks={G.ferryLinks?.map(link => ({
          id: link.id,
          from: link.from,
          to: link.to,
          cost: link.cost,
          ownerColor: link.owner ? G.players[link.owner]?.color ?? null : null,
        }))}
        adjacentLinks={G.adjacentLinks?.map(link => ({
          id: link.id,
          from: link.from,
          to: link.to,
          cost: link.cost,
          ownerColor: link.owner ? G.players[link.owner]?.color ?? null : null,
        }))}
        onLinkClick={ctx.phase === 'buildTrack' && (G.ferryLinks?.length > 0 || G.adjacentLinks?.length > 0) ? (linkType, linkId) => {
          debug.log(`[ActionPanelContainer] Link clicked: ${linkType} ${linkId}`);

          // タイル仮配置をクリア（リンク仮配置と排他）
          setTemporaryPlacement(null);

          // 同じリンクをクリックした場合は仮配置をクリア
          if (temporaryLinkPlacement?.linkType === linkType && temporaryLinkPlacement?.linkId === linkId) {
            setTemporaryLinkPlacement(null);
            return;
          }

          // バリデーション実行
          const args: BuildSpecialLinkArgs = { linkType, linkId };
          const validation = validateBuildSpecialLink(G as any, ctx, args);

          // リンクのコストを取得
          let cost = 0;
          if (linkType === 'ferry') {
            const link = G.ferryLinks?.find(l => l.id === linkId);
            cost = link?.cost ?? 0;
          } else {
            const link = G.adjacentLinks?.find(l => l.id === linkId);
            cost = link?.cost ?? 0;
          }

          // 仮配置状態を設定
          setTemporaryLinkPlacement({
            linkType,
            linkId,
            cost,
            validationError: validation.success ? null : (validation.message || validation.error || 'Unknown error'),
            validationErrorCode: validation.error,
          });
        } : undefined}
        temporaryLinkPlacement={temporaryLinkPlacement}
        highlightedGoods={ctx.phase === 'moveGoods' && selectedGoods ? selectedGoods : null}
        highlightedCityIds={(() => {
          // Phase 5 (moveGoods): 輸送先のみをハイライト（輸送元は商品ハイライトで表示）
          if (ctx.phase === 'moveGoods' && selectedGoods) {
            const moveGoodsProps = createMoveGoodsProps(G, isMyTurn, moves as any, selectedGoods, selectedRoute);
            const cityIds = moveGoodsProps.bestRoute
              ? [moveGoodsProps.bestRoute.destinationId]
              : null;
            debug.log('[highlightedCityIds]', cityIds);
            return cityIds;
          }
          // Phase 9 (refillGoods): Seattle 商品配置先をハイライト
          if (ctx.phase === 'refillGoods' && refillGoodsHighlightedCityIds.length > 0) {
            return refillGoodsHighlightedCityIds;
          }
          return null;
        })()}
        highlightIncome={ctx.phase === 'moveGoods' && selectedGoods ? (() => {
          const moveGoodsProps = createMoveGoodsProps(G, isMyTurn, moves as any, selectedGoods, selectedRoute);
          return moveGoodsProps.bestRoute?.income ?? null;
        })() : null}
        highlightedTrackHexCoords={ctx.phase === 'moveGoods' && selectedGoods ? (() => {
          const moveGoodsProps = createMoveGoodsProps(G, isMyTurn, moves as any, selectedGoods, selectedRoute);
          if (!moveGoodsProps.bestRoute?.linkDetails) return null;
          // linkDetailsからCompletedLinkを検索し、pathSegmentsから座標とEdgeペア情報を収集
          // ※ 特殊リンク（ferry-link-*, adjacent-link-*）はスキップ（別途highlightedSpecialLinkIdsで処理）
          // ※ 経路の進行方向に合わせてセグメントの順序とedgePairを調整
          // ※ 各セグメントにsegmentIndex（経路内の順序）を付与
          const hexCoords: Array<{ q: number; r: number; edgePair?: [number, number]; segmentIndex?: number }> = [];
          let segmentIndex = 0;  // 経路全体での順序を追跡
          debug.log('[highlightedTrackHexCoords] === START ===');
          debug.log('[highlightedTrackHexCoords] selectedGoods:', selectedGoods);
          debug.log('[highlightedTrackHexCoords] bestRoute.linkDetails:', moveGoodsProps.bestRoute.linkDetails);

          // 経路の進行方向を追跡（輸送元から開始）
          let currentCityId = selectedGoods.cityId;

          for (const linkDetail of moveGoodsProps.bestRoute.linkDetails) {
            const completedLink = G.completedLinks.find(cl => cl.id === linkDetail.id);
            // 特殊リンクはスキップ
            if (completedLink?.id.startsWith('ferry-link-') || completedLink?.id.startsWith('adjacent-link-')) {
              debug.log(`[highlightedTrackHexCoords] Skipping special link: ${completedLink?.id}`);
              // 特殊リンクでも次の都市へ進む
              if (completedLink) {
                currentCityId = completedLink.startId === currentCityId ? completedLink.endId : completedLink.startId;
              }
              continue;
            }

            // このリンクの進行方向を判定
            // currentCityId がこのリンクの startId と一致すれば順方向
            const isForward = completedLink?.startId === currentCityId;
            debug.log(`[highlightedTrackHexCoords] Link ${linkDetail.id}:`, {
              startId: completedLink?.startId,
              endId: completedLink?.endId,
              currentCityId,
              isForward,
              pathSegments: completedLink?.pathSegments,
              path: completedLink?.path
            });

            // 次のcurrentCityIdを更新
            if (completedLink) {
              currentCityId = isForward ? completedLink.endId : completedLink.startId;
            }

            // pathSegmentsがあればそれを使用、なければpathを使用（後方互換）
            if (completedLink?.pathSegments) {
              // 逆方向の場合、pathSegmentsを逆順にする
              const segments = isForward
                ? completedLink.pathSegments
                : [...completedLink.pathSegments].reverse();

              for (const segment of segments) {
                // edgePairも逆方向の場合は入れ替える（入口→出口の順序を維持）
                const edgePair: [number, number] | undefined = segment.edgePair
                  ? (isForward
                      ? segment.edgePair
                      : [segment.edgePair[1], segment.edgePair[0]])
                  : undefined;

                // 重複チェックなしで全セグメントを追加（segmentIndexで順序を管理）
                hexCoords.push({ q: segment.coord.q, r: segment.coord.r, edgePair, segmentIndex });
                debug.log(`[highlightedTrackHexCoords] Added segment[${segmentIndex}]: (${segment.coord.q},${segment.coord.r}) edgePair:`, edgePair, isForward ? '(forward)' : '(reversed)');
                segmentIndex++;
              }
            } else if (completedLink?.path) {
              // 後方互換: pathのみの場合は全体を強調（逆方向なら逆順）
              const coords = isForward ? completedLink.path : [...completedLink.path].reverse();
              for (const coord of coords) {
                hexCoords.push({ q: coord.q, r: coord.r, segmentIndex });
                debug.log(`[highlightedTrackHexCoords] Added path coord[${segmentIndex}]: (${coord.q},${coord.r}) NO edgePair`, isForward ? '(forward)' : '(reversed)');
                segmentIndex++;
              }
            }
          }
          debug.log('[highlightedTrackHexCoords] Final hexCoords:', hexCoords);
          debug.log('[highlightedTrackHexCoords] === END ===');
          return hexCoords.length > 0 ? hexCoords : null;
        })() : null}
        highlightedSpecialLinkIds={ctx.phase === 'moveGoods' && selectedGoods ? (() => {
          const moveGoodsProps = createMoveGoodsProps(G, isMyTurn, moves as any, selectedGoods, selectedRoute);
          if (!moveGoodsProps.bestRoute?.linkDetails) return null;
          // linkDetailsから特殊リンク（ferry-link-*, adjacent-link-*）を抽出
          const specialLinks: Array<{ linkType: 'ferry' | 'adjacent'; linkId: string }> = [];
          for (const linkDetail of moveGoodsProps.bestRoute.linkDetails) {
            const completedLink = G.completedLinks.find(cl => cl.id === linkDetail.id);
            if (completedLink?.id.startsWith('ferry-link-')) {
              const linkId = completedLink.id.replace('ferry-link-', '');
              specialLinks.push({ linkType: 'ferry', linkId });
            } else if (completedLink?.id.startsWith('adjacent-link-')) {
              const linkId = completedLink.id.replace('adjacent-link-', '');
              specialLinks.push({ linkType: 'adjacent', linkId });
            }
          }
          return specialLinks.length > 0 ? specialLinks : null;
        })() : null}
        seattleOverlay={createSeattleOverlayData(G)}
        placementError={refillGoodsError ? {
          coordinate: refillGoodsError.coordinate,
          message: 'Location full (2/2)',
        } : null}
        onShowRules={() => {
          setIsRulesOnlyMode(true);
          setShowStartModal(true);
        }}
        onResetGame={() => {
          window.location.href = BASE_PATH;
        }}
        onUndoToTurnStart={() => {
          moves.undoToTurnStart();
        }}
        canUndoToTurnStart={canUndoToTurnStart(ctx.phase, !!G.turnStartSnapshot)}
      />
      {/* ゲーム開始モーダル */}
      <GameStartModal
        isOpen={showStartModal}
        onStartGame={() => {
          setShowStartModal(false);
          setIsRulesOnlyMode(false);
        }}
        mapName={G.mapName}
        isRulesOnly={isRulesOnlyMode}
      />
      {/* ゲーム終了画面 */}
      {isGameOver && showGameEndModal && <GameEndScreen {...getGameEndProps()} />}
    </>
  );
}
